<template>
  <div class="i-job-test">
    <template v-if="isQuizCompleted || isQuizError">
      <iBackground />
    </template>

    <div class="navbar">
      <div
        v-if="data.showDiscountBanner"
        class="discount"
      >
        <div>
          <strong>Limited time only!</strong> Get up to <strong>30% off</strong> your
          <strong>first 4 coaching sessions!</strong>
        </div>
        <img
          class="close"
          :src="iconClose"
          @click="data.showDiscountBanner = false"
        />
      </div>
      <iNavbarSimple />
    </div>

    <div v-show="isQuizStarted">
      <div class="container-qubl">
        <iQublQuizCoaching
          ref="qublQuiz"
          :quiz-id="qublQuizId"
          @quiz-completed="onQuizCompleted"
          @quiz-success="onQuizSuccess"
          @quiz-error="onQuizError"
        />
      </div>
    </div>

    <div v-if="isQuizCompleted">
      <div class="container-loader">
        <iLoaderSimple :loading-animation-duration-ms="data.loadingAnimationDurationMs">
          <template #title>Success!</template>
          <template #subtitle>We are processing your response...</template>
        </iLoaderSimple>
      </div>
    </div>

    <div v-if="isQuizError">
      <template v-if="isEmailError">
        <div class="container-error-email">
          <iQuizEmailError @submit-new-email="submitNewEmail" />
        </div>
      </template>

      <template v-else>
        <div class="container-error-general">
          <iBaseErrorCard
            button-text="Back to Jobtest.org"
            :error-message-one-h-t-m-l="data.quizErrorMessageHTML"
            @clicked-logo="goToHome()"
            @clicked-button="goToHome()"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue"
import { useRouter } from "vue-router"

import { useUserStore } from "@/stores/user"

import { GlobalFunctions } from "@/helpers/globalFunctions"
import { GlobalVariables } from "@/helpers/globalVariables"
import QuizService from "@/services/quizService"

import iBaseErrorCard from "@/components/base/BaseErrorCard.vue"
import iBackground from "@/components/global/Background.vue"
import iLoaderSimple from "@/components/global/LoaderSimple.vue"
import iNavbarSimple from "@/components/global/NavbarSimple.vue"

import iQublQuizCoaching from "@/views/jobTest/sections/QublQuizCoaching.vue"
import iQuizEmailError from "@/views/jobTest/sections/QuizEmailError.vue"

// ASSETS
import iconClose from "@/assets/icons/icon-close.svg"

// TYPES
import CoachingStatus from "@/shared/enums/CoachingStatus"
import type { ApiError } from "@/shared/apiClient"
import type { QuizData } from "@/types/quizData"

// CONSTANTS
const qublQuizId = GlobalFunctions.getQublQuizIdForCoaching()

// STORES
const userStore = useUserStore()

// ROUTER
const router = useRouter()

// REFS
const qublQuiz = ref(null)

// DATA
const data = reactive({
  showDiscountBanner: true,
  loadingAnimationDurationMs: 4000,
  quizData: {} as QuizData,
  quizStatus: QuizService.QUIZ_STATUSES.STARTED,
  quizErrorMessageHTML: QuizService.ERROR_MESSAGES["DEFAULT"],
  userName: "",
  userZipCode: ""
})

// COMPUTED

const isQuizStarted = computed(() => {
  return data.quizStatus === QuizService.QUIZ_STATUSES.STARTED
})

const isQuizCompleted = computed(() => {
  return data.quizStatus === QuizService.QUIZ_STATUSES.COMPLETED
})

const isQuizError = computed(() => {
  return data.quizStatus === QuizService.QUIZ_STATUSES.ERROR
})

const isEmailError = computed(() => {
  return data.quizErrorMessageHTML === QuizService.ERROR_MESSAGES[403]
})

// FUNCTIONS
function goToHome() {
  window.location.href = GlobalVariables.urls.website.path
}

function onQuizCompleted() {
  data.quizStatus = QuizService.QUIZ_STATUSES.COMPLETED
}

function onQuizSuccess() {
  try {
    userStore.userData.coachingStatus = CoachingStatus.FINISHED
    router.push({
      name: GlobalVariables.urls.coaching.children.loading.routeName
    })
  } catch (error) {
    console.error("Error redirecting to loading page", error)
  }
}

function onQuizError(error: ApiError, quizData: QuizData) {
  if (error.status === 400) data.quizErrorMessageHTML = QuizService.ERROR_MESSAGES[400]
  else if (error.status === 401) data.quizErrorMessageHTML = QuizService.ERROR_MESSAGES[401]
  else if (error.status === 403) {
    data.quizErrorMessageHTML = QuizService.ERROR_MESSAGES[403]
    data.quizData = quizData
  } else if (error.status === 500) data.quizErrorMessageHTML = QuizService.ERROR_MESSAGES[500]
  else data.quizErrorMessageHTML = QuizService.ERROR_MESSAGES["DEFAULT"]

  data.quizStatus = QuizService.QUIZ_STATUSES.ERROR
}

function submitNewEmail(email: string) {
  if (!qublQuiz.value) return
  data.quizData.answers.email = email
  // @ts-expect-error - qublQuiz.value is a ref, and it's not typed
  qublQuiz.value.processQuizCompletionWithNewEmail(data.quizData)
}
</script>

<style scoped lang="scss">
.i-job-test {
  .navbar {
    @include flex(column, start, stretch);

    position: sticky;
    top: 0;

    .discount {
      @include flex(column, center, center);
      @include text-xs;

      position: relative;
      background-color: $brand-opacity60;
      padding: 5px 35px;
      font-weight: 500;
      text-align: center;

      @media screen and (min-width: $breakpoint-xs) {
        @include text-sm;
      }

      @media screen and (min-width: $breakpoint-sm) {
        @include text-md;
      }

      .close {
        position: absolute;
        right: 10px;
        width: 18px;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;

        @media screen and (min-width: $breakpoint-xs) {
          right: 12px;
        }

        @media screen and (min-width: $breakpoint-sm) {
          right: 16px;
        }

        @media screen and (min-width: $breakpoint-md) {
          right: 25px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          right: 30px;
        }

        &:hover {
          background: #0000000f;
        }
      }
    }
  }

  .container-qubl,
  .container-loader,
  .container-error-email,
  .container-error-general {
    height: calc(100vh - 90px);
    margin-left: auto;
    margin-right: auto;
    width: 90%;
  }

  .container-qubl {
    margin-top: 10px;
    max-width: $section-max-width;
  }

  .container-loader {
    margin-top: -60px;
    max-width: $section-max-width;
  }

  .container-error-email {
    @include flex($justify: center, $align: center);

    max-width: 550px;
    margin-top: -30px;
  }

  .container-error-general {
    @include flex($justify: center, $align: center);

    max-width: 450px;
    margin-top: -30px;
  }
}
</style>
